import {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { Card, CardContent, Typography, Box, Avatar } from "@mui/material";
import "swiper/scss";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { slides } from "./Slides.jsx";
import { useTranslation } from "react-i18next";

const Slider = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { onSlideChange } = props;
  const swiperRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentSlide, setCurrentSlide] = useState(1);

  const handleSlideChange = (index) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
      setCurrentSlide(index + 1);
    }
  };

  const handleSwiperSlideChange = (swiper) => {
    setCurrentSlide(swiper.activeIndex + 1); 
    if (typeof onSlideChange === "function") {
      onSlideChange(swiper.activeIndex);
    }
  };

  useImperativeHandle(ref, () => ({
    slideNext: () => swiperRef.current?.slideNext(),
    slidePrev: () => swiperRef.current?.slidePrev(),
  }));

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      {windowWidth > 800 ? (
        <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={30}
        slidesPerView={1}
        // autoplay={{ delay: 10000 }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        // В onSlideChange вызываем нашу функцию, 
        // которая синхронизирует state и вызывает onSlideChange из props
        onSlideChange={handleSwiperSlideChange}
      >
          {slides.map((slide, index) => (
            <SwiperSlide key={index}>
              <Card
                sx={{
                  width: "90vw",
                  height: 720,
                  margin: "auto",
                  padding: 0,
                  backgroundColor: "#39C5CA !important",
                }}
              >
                <CardContent sx={{ padding: 0, height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        padding: "48px",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "left",
                          marginBottom: 1,
                          fontSize: "clamp(24px, 2.5vw, 48px)",
                          color: "#181818",
                          fontFamily: "Neue Haas Grotesk Display Pro",
                          fontStyle: "normal",
                          fontWeight: 600,
                          lineHeight: "clamp(24px, 2.5vw, 48px)",
                          letterSpacing: "-1.44px",
                        }}
                      >
                        {`\u201C${t(slide.text)}\u201D`}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: "left",
                          fontWeight: "bold",
                          fontSize: "32px",
                          color: "#181818",

                          fontFamily: "Neue Haas Grotesk Display Pro",
                          fontStyle: "normal",
                          fontWeight: 600,
                          lineHeight: "32px",
                          letterSpacing: "-0.96px",
                        }}
                      >
                        {slide.name}
                      </Typography>
                    </Box>
                    <Box>
                      <Avatar
                        src={slide.avatar}
                        sx={{
                          width: 500,
                          height: 500,
                          filter: "grayscale(100%)",
                          borderRadius: 0,
                          objectFit: "cover",
                          "& img": {
                            objectPosition: "top",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "24px",
                          color: "#181818",
                          fontFamily: "Neue Haas Grotesk Display Pro",
                          fontSize: "48px",
                          fontStyle: "normal",
                          fontWeight: 600,
                          lineHeight: "48px",
                          letterSpacing: "-1.44px",
                          cursor: "pointer",
                        }}
                      >
                        {slides.map((_, index) => (
                          <div
                            key={index}
                            onClick={() => handleSlideChange(index)}
                            style={{
                              opacity: currentSlide === index + 1 ? 1 : 0.32,
                              transition: "opacity 0.3s ease-in-out",
                            }}
                          >
                            {index + 1 < 10 ? `0${index + 1}` : index + 1}
                          </div>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          centeredSlides={true}
          // autoplay={{ delay: 5000 }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          // Здесь тоже используем нашу общую функцию
          onSlideChange={handleSwiperSlideChange}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index} className="custom-slide">
              <Card
                sx={{
                  maxWidth:
                    '90vw',
                  height: 774,
                  margin: "5vw",
                  backgroundColor: "#39C5CA !important",
                  transition: "transform 0.5s ease, opacity 0.5s ease",
                  transform:
                    index === swiperRef.current?.activeIndex
                      ? "scale(1)"
                      : "scale(0.9)",
                  opacity: index === swiperRef.current?.activeIndex ? 1 : 0.7,
                  overflow: "hidden",
                  boxShadow:
                    index === swiperRef.current?.activeIndex
                      ? "0 8px 20px rgba(0, 0, 0, 0.15)"
                      : "none",
                }}
              >
                <CardContent sx={{ padding: 0, height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      flex: 1,
                      height: "100%",
                    }}
                  >
                    <Avatar
                      src={slide.avatar}
                      sx={{
                        width: "100%",
                        height: '70vw',
                        marginRight: 3,
                        filter: "grayscale(100%)",
                        borderRadius: 0,
                        objectFit: "cover",
                        "& img": {
                          objectPosition: "top",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        height: "100%",
                        paddingTop: "33px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "left",
                          marginBottom: 3,
                          color: "#181818",
                          fontFamily: "Neue Haas Grotesk Display Pro",
                          fontSize: "clamp(18px, 1.25vw, 24px)",
                          fontStyle: "normal",
                          fontWeight: 600,
                          lineHeight: "clamp(18px, 1.25vw, 24px)",
                          letterSpacing: "-0.66px",
                          padding: "0 24px",
                        }}
                      >
                        {`\u201C${t(slide.text)}\u201D`}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: "left",
                          color: "#181818",
                          fontFamily: "Neue Haas Grotesk Display Pro",
                          fontSize: "clamp(18px, 1.25vw, 24px)",
                          fontStyle: "normal",
                          fontWeight: 600,
                          lineHeight: "clamp(18px, 1.25vw, 24px)",
                          letterSpacing: "-0.96px",
                          padding: "0 24px",
                        }}
                      >
                        {slide.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          padding: "0 5vw",
                        }}
                      >
                        {slides.map((_, index) => (
                          <div
                            key={index}
                            onClick={() => handleSlideChange(index)}
                            style={{
                              opacity: currentSlide === index + 1 ? 1 : 0.32,
                              transition: "opacity 0.3s ease-in-out",
                              color: "#181818",
                              fontFamily: "Neue Haas Grotesk Display Pro",
                              fontSize: "clamp(18px, 1.67vw, 32px)",
                              fontStyle: "normal",
                              fontWeight: 600,
                              lineHeight: "48px",
                              letterSpacing: "-1.44px",
                            }}
                          >
                            {index + 1 < 10 ? `0${index + 1}` : index + 1}
                          </div>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </>
  );
});

export default Slider;
